import { DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined } from "@ant-design/icons"
import type { TableColumnsType, TablePaginationConfig, TableProps } from "antd"
import { Button, Card, Input, message, Modal, Select, Space, Table, Tabs, Tooltip } from "antd"
import Title from "antd/es/typography/Title"
import queryString from "query-string"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router"
import { Link } from "react-router-dom"
import Filters, { FilterComponent } from "~/components/Filters"
import { Paths } from "~/constants"
import { t } from "~/languages/i18n"
import { PaginationMeta } from "~/types/types"
import { formatDatePost } from "~/utils/formatDate"
import { normalizeText } from "~/utils/helper"
import { deleteParentQuestion, fetchParentQuestions, fetchParentQuestionStats, updateParentQuestion } from "../services/parent-question"

const List: React.FC = () => {
  const [dataItem, setDataItem] = useState<any[]>([])
  const [allData, setAllData] = useState<any[]>([])
  const filterRef = useRef<{ filterFunction: () => void } | null>(null)
  const [sortPaginationValues, setSortPaginationValues] = useState<{ [key: string]: string } | null>(null)
  const [needRefresh, setNeedRefresh] = useState<boolean>(false)
  const [filterValues, setFilterValues] = useState<{ [key: string]: string } | null>(null)
  const [defaultFilterValues, setDefaultFilterValues] = useState<{ [key: string]: any } | null>(null)
  const [filterTabFaq, setFilterTabFaq] = useState("all")
  const [statusCounts, setStatusCounts] = useState<{ all: number; PENDING: number; APPROVED: number; REJECTED: number }>({
    all: 0,
    PENDING: 0,
    APPROVED: 0,
    REJECTED: 0,
  })
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [bulkAction, setBulkAction] = useState<string>("")
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [metaData, setMetaData] = useState<PaginationMeta>()
  const [totalItem, setTotalItem] = useState(0)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editingAnswer, setEditingAnswer] = useState("")

  const permissions = JSON.parse(localStorage.getItem("permissions") || "{}")

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(selectedRowKeys)
    },
  }

  const fetchStats = async () => {
    try {
      const stats = await fetchParentQuestionStats()
      if (stats) {
        setStatusCounts({
          all: stats.total || 0,
          PENDING: stats.PENDING || 0,
          APPROVED: stats.APPROVED || 0,
          REJECTED: stats.REJECTED || 0,
        })
      }
    } catch (error) {
      message.error(t("Failed to fetch stats"))
    }
  }

  useEffect(() => {
    fetchStats()
  }, [needRefresh])

  const fetchFaqsData = async (searchTerms?: { [key: string]: string }) => {
    setLoading(true)
    setDataItem([])
    try {
      const defaultPagination = {
        page: "1",
        pageSize: "20",
      }
      const allResponse = await fetchParentQuestions({ ...defaultPagination, ...searchTerms })
      const response = allResponse && allResponse.data
      const meta = allResponse && allResponse.meta
      setMetaData(meta)

      if (response && response.length > 0) {
        setAllData(response)
        setDataItem(response)
        setTotalItem(meta?.pagination?.total || 0)
      }

      return response
    } catch (error) {
      message.error(t("Failed to fetch FAQs. Please try again."))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const filteredData = filterTabFaq === "all" ? allData : allData.filter((item: any) => item.attributes?.status === filterTabFaq)
    setDataItem(filteredData)
    setTotalItem(filteredData.length)
  }, [filterTabFaq, allData])

  const onSearch = useCallback(
    (filters: object) => {
      const values: { [key: string]: string } = {}

      Object.keys(filters).forEach(key => {
        const value = filters[key as keyof typeof filters]
        if (typeof value === "string") {
          values[key] = value
        }
      })

      if (!values) {
        message.error("Values are undefined")
        return
      }

      let newValue = { ...values }

      if (values.search) {
        newValue = { ...newValue, search: normalizeText(values.search) }
      }

      const searchParams = queryString.stringify(newValue, { encode: false })
      const newUrl = searchParams.length > 0 ? `${location.pathname}?${searchParams}` : location.pathname
      navigate(newUrl)
    },
    [navigate],
  )

  useEffect(() => {
    const searchParams = queryString.parse(location.search)
    const filterValues: { [key: string]: string } = {}
    const sortPaginationValues: { [key: string]: string } = {}

    for (const key of Object.keys(searchParams)) {
      const value = searchParams[key]
      if (value && typeof value === "string") {
        if (key === "page" || key === "limit" || value === "asc" || value === "desc") {
          sortPaginationValues[key] = value
        } else {
          filterValues[key] = value
        }
      }
    }

    setSortPaginationValues(sortPaginationValues)
    setFilterValues(filterValues)
    setDefaultFilterValues(filterValues)
  }, [location.search])

  useEffect(() => {
    if (!filterValues && !sortPaginationValues) return

    if (filterValues || sortPaginationValues) {
      const params = { ...filterValues, ...sortPaginationValues }
      fetchFaqsData(params)
    }
  }, [filterValues, sortPaginationValues, needRefresh])

  const handleEdit = (id: number) => {
    navigate(`${Paths.PARENT_QUESTIONS.EDIT.PATH}/${id}`)
  }

  const handleDelete = async (id: number) => {
    Modal.confirm({
      title: t("Confirm Delete"),
      content: t("Are you sure you want to delete this FAQ?"),
      okText: t("Delete"),
      cancelText: t("Cancel"),
      onOk: async () => {
        try {
          setLoading(true)
          await deleteParentQuestion(id)
          fetchFaqsData()
          message.success(t("FAQ deleted successfully!"))
        } catch {
          message.error("Handle Delete Error")
        }
      },
    })
  }

  const handleOpenReplyModal = (record: any) => {
    setEditingId(record.id)
    setEditingAnswer(record.attributes.answer || "")
  }

  const handleSaveAnswer = async () => {
    if (!editingId) return
    try {
      setLoading(true)
      await updateParentQuestion(editingId, {
        answer: editingAnswer,
        status: "APPROVED"
      })
      message.success(t("Reply submitted successfully"))
      setEditingId(null)
      fetchFaqsData()
    } catch {
      message.error(t("Failed to submit reply"))
    } finally {
      setLoading(false)
    }
  }

  const handleCancelEdit = () => {
    setEditingId(null)
    setEditingAnswer("")
  }

  const handleChangeTab = (key: string) => {
    setFilterTabFaq(key)
  }

  const onChange: TableProps<any>["onChange"] = (pagination: TablePaginationConfig) => {
    setSortPaginationValues({
      page: pagination.current ? pagination.current.toString() : "1",
      pageSize: pagination.pageSize?.toString() || "20",
    })
  }

  const onRefresh = () => {
    if (filterRef.current) {
      filterRef.current.filterFunction()
    }
    setSortPaginationValues(null)
    setNeedRefresh(!needRefresh)
  }

  const columns: TableColumnsType<any> = [
    {
      title: t("Name"),
      dataIndex: ["attributes", "author_name"],
      key: "author_name",
      width: "15%",
      render: (text) => <span className="font-normal flex justify-center">{text?.trim() || "-"}</span>,
    },
    {
      title: t("Question"),
      dataIndex: ["attributes", "question"],
      key: "question",
      showSorterTooltip: false,
      sorter: (a, b) => a.attributes.question.localeCompare(b.attributes.question),
      width: "20%",
      render: (_, record) => (
        <Link
          to={`${Paths.PARENT_QUESTIONS.EDIT.PATH}/${record.id}`}
          className="text-[#1677ff] cursor-pointer transition duration-300 hover:text-[#0b5ed7] hover:underline text-base"
        >
          {record.attributes.question}
        </Link>
      ),
    },
    {
      title: t("Answer"),
      dataIndex: ["attributes", "answer"],
      key: "answer",
      width: "30%",
      render: (answer: string, record: any) => {
        const plainText = answer?.trim() || ""
        const isEditing = editingId === record.id

        if (isEditing) {
          return (
            <div className="flex flex-col gap-2">
              <Input.TextArea
                value={editingAnswer}
                onChange={(e) => setEditingAnswer(e.target.value)}
                rows={3}
                autoFocus
              />
              <Space size="small">
                <Button type="primary" size="small" onClick={handleSaveAnswer} loading={loading}>
                  {t("Save")}
                </Button>
                <Button size="small" onClick={handleCancelEdit}>
                  {t("Cancel")}
                </Button>
              </Space>
            </div>
          )
        }

        return (
          <div className="flex flex-col gap-1">
            <span className="text-base line-clamp-2">{plainText}</span>
            <Button type="link" size="small" onClick={() => handleOpenReplyModal(record)} className="h-auto p-0">
              {plainText ? t("Edit") : t("Reply")}
            </Button>
          </div>
        )
      },
      showSorterTooltip: false,
      sorter: (a, b) => (a.attributes?.answer || "").localeCompare(b.attributes?.answer || ""),
    },
    {
      title: t("Create At"),
      dataIndex: ["attributes"],
      key: "createdAt",
      width: "10%",
      render: (data) => {
        const formattedDate = formatDatePost(data.createdAt)
        return <span className="font-normal">{formattedDate}</span>
      },
    },
    {
      title: t("Status"),
      key: "status",
      width: "10%",
      render: (_, record) => {
        const statusOptions = [
          { value: "PENDING", label: t("Pending"), color: "orange" },
          { value: "APPROVED", label: t("Approved"), color: "green" },
          { value: "REJECTED", label: t("Rejected"), color: "red" },
        ]
        return (
          <Select
            value={record.attributes.status}
            onChange={async (value) => {
              try {
                await updateParentQuestion(record.id, { status: value })
                setDataItem(prevData =>
                  prevData.map(item =>
                    item.id === record.id
                      ? { ...item, attributes: { ...item.attributes, status: value } }
                      : item
                  )
                )
                message.success(t("Status updated successfully"))
              } catch {
                message.error(t("Failed to update status"))
              }
            }}
            style={{ width: 140 }}
            options={statusOptions.map(opt => ({
              value: opt.value,
              label: (
                <span style={{ color: opt.color, fontWeight: 500 }}>
                  {opt.label}
                </span>
              ),
            }))}
          />
        )
      },
    },
    {
      title: t("Action"),
      key: "action",
      width: "15%",
      render: (_, record) => (
        <Space size="small" className="justify-center items-center">
          <Tooltip title={t("Edit")}>
            <Button
              type="primary"
              icon={<EditOutlined />}
              className="bg-blue-500 px-4 py-2 rounded"
              onClick={() => handleEdit(record.id)}
            />
          </Tooltip>

          <Tooltip title={t("Delete")}>
            <Button danger type="primary" icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)} />
          </Tooltip>
        </Space>
      ),
    }
  ]

  return (
    <>
      <div className="p-4">
        <div className="flex space-x-4 items-center">
          <Title level={2} className="m-0">
            {t("Parent Question")}
          </Title>
          <Button
            onClick={() => navigate(Paths.PARENT_QUESTIONS.CREATE.PATH)}
            color="primary"
            variant="outlined"
            icon={<PlusOutlined />}
          >
            {t("Add New Parent Question")}
          </Button>
        </div>
        <div className="flex justify-between mt-3">
          <Tabs
            defaultActiveKey="all"
            activeKey={filterTabFaq}
            onChange={handleChangeTab}
            items={[
              { label: `${t("All")} (${statusCounts.all})`, key: "all" },
              { label: `${t("Pending")} (${statusCounts.PENDING})`, key: "PENDING" },
              { label: `${t("Approved")} (${statusCounts.APPROVED})`, key: "APPROVED" },
              { label: `${t("Rejected")} (${statusCounts.REJECTED})`, key: "REJECTED" },
            ]}
          />
          <Space className=" flex justify-end">
            <Button onClick={onRefresh} icon={<ReloadOutlined />}>
              {t("Refresh")}
            </Button>
          </Space>
        </div>
        <Card type="inner" style={styles.card}>
          <Filters
            filterRef={filterRef}
            defaultFilterValues={defaultFilterValues}
            onSubmit={onSearch}
            filters={
              [
                {
                  label: t("Search"),
                  placeholder: t("Find Parent Question Question"),
                  name: "question",
                  type: "text",
                  customStyle: "2xl:w-[30rem] w-[20rem]",
                },
              ] as FilterComponent[]
            }
          />
        </Card>
        <Table
          className="mt-3 border border-[rgb(229 229 229)]"
          rowKey="id"
          rowSelection={rowSelection}
          columns={columns}
          loading={loading}
          dataSource={dataItem}
          onChange={onChange}
          pagination={{
            pageSize: 20,
            total: (metaData && metaData?.pagination?.total) || 0,
          }}
          rowClassName={(record, index) => (index % 2 === 0 ? "bg-[#f6f7f7] " : "bg-white")}
        />
      </div>
    </>
  )
}

export default List

const styles = {
  card: {
    marginTop: "1.25rem",
    marginBottom: "1.25rem",
  },
}
